import { Grid } from "@mui/material";
import { Box } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import {
  Autocomplete,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  ExpandMoreIcon,
  ChevronLeftIcon,
  IconButton,
  Divider,
  Tooltip,
} from "../../mui";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import FileUpload from "../../components/FileUpload";
import React, { useEffect, useState } from "react";
import ShipmentInquiriesService from "../../services/ShipmentInquiriesService";
import { snackbarActions } from "../../Store/snackbar";
import { useDispatch } from "react-redux";
import { Dialog } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const linkStyle = {
  fontSize: "14px",
  color: "#1976d2",
  fontWeight: "400",
  marginRight: "6px",
};

const UploadManifest = ({ value, setValue, formData }) => {
  const [files, setFiles] = useState(null);
  const [list, setList] = useState([]);
  const [shipment, setShipment] = useState("");
  const [shipmentDetails, setShipmentDetails] = useState([]);
  const [inventoryCodes, setInventoryCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmStatusModal, setShowConfirmStatusModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isShipped, setIsShipped] = useState(false);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [unshippedCount, setUnshippedCount] = useState(0);
  const dispatch = useDispatch();
  const message = "Please select at least one inventory code.";

  const handleBack = () => {
    setValue("two");
  };

  const cancelAssignment = () => {
    setShipment("");
  };

  const handleShipmentChange = (value) => {
    setShipment(value);
  };

  const commonDispatch = (type, message) => {
    dispatch(
      snackbarActions.showNotification({
        snackbarOpen: true,
        snackbarType: type,
        snackbarMessage: message,
      })
    );
  };

  const assignUnassignInventoryCodes = () => {
    let selectedCodes = [];
    selectedCodes = inventoryCodes
      .filter((code) => Object.values(code)[0] === true)
      .map((code) => Object.keys(code)[0]);

    if (selectedCodes.length === 0) {
      commonDispatch("warning", message);
      return;
    }

    if (shipment) {
      const payload = {
        inquiry_id: formData.inquiry_id,
        shipment_name: shipment,
        inventory_codes: selectedCodes,
      };

      ShipmentInquiriesService.assignUnassignInventoryCodes(payload)
        .then((resp) => {
          getUnassignedItems();
          getShipmentDetailsList();
          setShipment("");
          commonDispatch("success", resp.data);
        })
        .catch((err) => {
          commonDispatch("error", err.message);
        });
    }
  };

  const unassignInventoryCodes = (shipmentName) => {
    let selectedAssignedCodes = [];
    // For unassigning, get selected codes from the shipment's assigned inventory codes
    const currentShipment = shipmentDetails.find(
      (detail) => detail.shipment_name === shipmentName
    );

    if (currentShipment) {
      selectedAssignedCodes = currentShipment.assigned_inventory_codes
        .filter((code) => Object.values(code)[0] === true)
        .map((code) => Object.keys(code)[0]);
    }

    if (selectedAssignedCodes.length === 0) {
      commonDispatch("warning", message);
      return;
    }

    if (shipmentName) {
      const payload = {
        inquiry_id: formData.inquiry_id,
        shipment_name: shipmentName,
        inventory_codes: selectedAssignedCodes,
      };

      ShipmentInquiriesService.unassignInventoryCodes(payload)
        .then((resp) => {
          getUnassignedItems();
          getShipmentDetailsList();
          commonDispatch("success", resp.data);
        })
        .catch((err) => {
          commonDispatch("error", err.message);
        });
    }
  };

  const createShipment = () => {
    ShipmentInquiriesService.addShipment({ inquiry_id: formData.inquiry_id })
      .then((resp) => {
        getShipmentList();
        commonDispatch("success", resp.data.message);
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  const getUnassignedItems = () => {
    ShipmentInquiriesService.getUnassignedItems(formData.inquiry_id)
      .then((resp) => {
        const codes = [];
        resp.data?.forEach((item) => {
          if (!item.is_assigned) {
            codes.push({ [item.inventory_code]: false });
          }
        });
        setInventoryCodes(codes);
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  const getShipmentList = () => {
    ShipmentInquiriesService.getShipmentList(formData.inquiry_id)
      .then((resp) => {
        const shipments = [];
        resp.data?.forEach((shipment) => {
          shipments.push(shipment.shipment_name);
        });
        setList(shipments);
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  const fileUploadAssignment = () => {
    const payload = new FormData();
    payload.append("file", files);
    payload.append("inquiry_id", formData.inquiry_id);
    payload.append("shipment_name", shipment);

    if (files && shipment) {
      ShipmentInquiriesService.assignInventoryCodesUploadFile(payload)
        .then((resp) => {
          getUnassignedItems();
          getShipmentDetailsList();
          setFiles(null);
          setShipment("");
          commonDispatch("success", resp.data.message);
        })
        .catch((err) => {
          commonDispatch("error", err.message || "Failed to upload the file");
        });
    }
  };

  const getShipmentDetailsList = () => {
    ShipmentInquiriesService.getShipmentDetailsList(formData.inquiry_id)
      .then((resp) => {
        const initializedShipments = resp.data.shipments.map((shipment) => ({
          ...shipment,
          assigned_inventory_codes: shipment.assigned_inventory_codes.map(
            (code) => ({ [code]: false })
          ),
        }));
        setShipmentDetails(initializedShipments);

        const unshipped = initializedShipments.filter(
          (s) => s.shipment_status === false
        );
        setUnshippedCount(unshipped.length);
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  const downloadManifestHandler = (file_key) => {
    if (file_key) {
      ShipmentInquiriesService.downloadManifestFile(file_key)
        .then((resp) => {
          commonDispatch(
            "success",
            "Successfully downloaded the Manifest file"
          );
          const url = resp.data.presigned_url;
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Manifest-File");
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          commonDispatch(
            "error",
            err.message || "Failed to download the manifest"
          );
        });
    }
  };

  const uploadManifestHandler = (event, shipment_name) => {
    if (event?.target?.files[0]) {
      const payload = new FormData();
      payload.append("file", event?.target?.files[0]);
      payload.append("inquiry_id", formData.inquiry_id);
      payload.append("shipment_name", shipment_name);
      ShipmentInquiriesService.uploadManifestFile(payload)
        .then((resp) => {
          getUnassignedItems();
          getShipmentDetailsList();
          commonDispatch("success", resp.data);
        })
        .catch((err) => {
          commonDispatch(
            "error",
            err.message || "Failed to upload the manifest"
          );
        });
    }
  };

  const handleMarkAsShipped = (status) => {
    const payload = {
      shipment_name: selectedShipment.shipment_name,
      inquiry_id: selectedShipment.inquiry_id,
      shipment_fulfillment_status: status,
    };

    const endpoint = isShipped
      ? "/order/mark_shipped"
      : "/order/enable_shipment_inquiries";

    ShipmentInquiriesService.markShippedUnshipped(endpoint, payload)
      .then((resp) => {
        commonDispatch(
          "success",
          resp.data ? resp.data : "Shipment updated successfully"
        );
        getShipmentDetailsList();
        setShowConfirmModal(false);
        setShowConfirmStatusModal(false);
      })
      .catch((err) => {
        getShipmentDetailsList();
        commonDispatch(
          "warning",
          err?.response?.data?.detail || "Action failed"
        );
        setShowConfirmModal(false);
        setShowConfirmStatusModal(false);
      });
  };
  const handleSaveTracking = (shipmentName) => {
    const trackingId = trackingInputs[shipmentName]?.trackingId || "";
    const trackingUrl = trackingInputs[shipmentName]?.trackingUrl || "";
    const comment = trackingInputs[shipmentName]?.comment || "";
    const payload = {
      shipment_name: shipmentName,
      tracking_id: trackingId,
      tracking_url: trackingUrl,
      inquiry_id: formData.inquiry_id,
      comment: comment,
    };
    ShipmentInquiriesService.saveTrackingInfo(payload)
      .then((resp) => {
        commonDispatch("success", resp.data);
        setTrackingInputs((prev) => ({
          ...prev,
          [shipmentName]: { trackingId: "", trackingUrl: "", comment: "" },
        }));
        getShipmentDetailsList();
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  const handleCopyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    commonDispatch("success", "Tracking URL copied to clipboard!");
  };

  useEffect(() => {
    if (value === "five") {
      getUnassignedItems();
      getShipmentList();
      getShipmentDetailsList();
      setShipment("");
    }
  }, [value]);

  useEffect(() => {
    fileUploadAssignment();
  }, [files]);

  useEffect(() => {
    uploadManifestHandler();
  }, []);

  return (
    <>
      {value === "five" && (
        <>
          {/* Manage Shipment Section */}
          <Grid container spacing={1} columns={12} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography variant="h5" color="secondary">
                Manage Shipment
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  position: "relative",
                  float: "right",
                  padding: "5px 10px 5px 0px",
                }}
              >
                <ChevronLeftIcon />
                Go Back
              </Button>
            </Grid>
          </Grid>

          
          <Grid container spacing={2} columns={12}>
            <Grid item xs={4}>
              <Box>
                <Typography
                  variant="h6"
                  color="secondary"
                  sx={{ textAlign: "center", marginBottom: "10px" }}
                >
                  Unassigned Items
                </Typography>
                <Divider style={{ width: "100%" }}></Divider>
                <Box>
                  {inventoryCodes.length === 0 ? (
                    <Typography
                      sx={{ textAlign: "center", fontSize: "12px", mt: 3 }}
                    >
                      No Item Available
                    </Typography>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search Inventory Code"
                        variant="outlined"
                        margin="dense"
                        onChange={(e) => {
                          // Just store the search term, don't modify the inventory codes
                          setSearchTerm(e.target.value);
                        }}
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={inventoryCodes.every(
                                (code) => Object.values(code)[0] === true
                              )}
                              indeterminate={
                                inventoryCodes.some(
                                  (code) => Object.values(code)[0] === true
                                ) &&
                                inventoryCodes.some(
                                  (code) => Object.values(code)[0] === false
                                )
                              }
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setInventoryCodes((prevCodes) =>
                                  prevCodes.map((code) => {
                                    const key = Object.keys(code)[0];
                                    return { [key]: isChecked };
                                  })
                                );
                              }}
                            />
                          }
                          label="Select All"
                        />
                      </Box>

                      <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                        {inventoryCodes
                          .filter((code) => {
                            const key = Object.keys(code)[0];
                            return searchTerm
                              ? key
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase())
                              : true;
                          })
                          .map((code, index) => {
                            const codeKey = Object.keys(code)[0];
                            const isChecked = code[codeKey];
                            return (
                              <FormControlLabel
                                key={codeKey}
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={() => {
                                      setInventoryCodes((prevCodes) => {
                                        return prevCodes.map((item) => {
                                          const itemKey = Object.keys(item)[0];
                                          if (itemKey === codeKey) {
                                            return { [itemKey]: !isChecked };
                                          }
                                          return item;
                                        });
                                      });
                                    }}
                                  />
                                }
                                label={codeKey}
                              />
                            );
                          })}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Box>
                <Button
                  variant="contained"
                  onClick={createShipment}
                  sx={{ marginBottom: "5px" }}
                >
                  Create New Shipment
                </Button>
              </Box>

              <Divider style={{ width: "100%" }}></Divider>

              {/* Shipment Droplist */}
              <Autocomplete
                id="free-solo-demo"
                freeSolo
                size="small"
                margin="dense"
                options={list}
                onChange={(e, new_value) => handleShipmentChange(new_value)}
                renderInput={(params) => (
                  <TextField
                    margin="dense"
                    {...params}
                    label="Select Shipment"
                  />
                )}
                value={shipment}
              />

              <Box sx={{ mt: 2, bt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={cancelAssignment}
                  disabled={!shipment}
                >
                  Cancel
                </Button>

                <Button
                  sx={{ float: "right" }}
                  variant="contained"
                  onClick={() => assignUnassignInventoryCodes()}
                  disabled={!shipment}
                >
                  Assign
                </Button>
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="h6"
                color="secondary"
                sx={{ textAlign: "center", marginBottom: "10px" }}
              >
                Upload Manifest File
              </Typography>

              <Divider style={{ width: "100%" }}></Divider>
              <Box sx={{ mt: "20px" }}>
                <FileUpload
                  files={files}
                  setFiles={setFiles}
                  accept={["csv", "xlsx"]}
                  tabIndex={0}
                  role="region"
                  aria-label="File upload section"
                  disabled={
                    !shipment ||
                    inventoryCodes.some(
                      (code) => Object.values(code)[0] === true
                    )
                  }
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2} columns={12}>
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="h6"
                  color="secondary"
                  sx={{ textAlign: "center", marginBottom: "10px" }}
                >
                  Shipments
                </Typography>
                <Divider style={{ width: "100%" }}></Divider>

                {/* Shipment Cards Container */}

                <Grid container spacing={2}>
                  {shipmentDetails.map(
                    (shipment, index) =>
                      shipment &&
                      shipment.assigned_inventory_codes.length > 0 && (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: "4px",
                              p: 2,
                              mt: "10px",
                              mb: 2,
                              backgroundColor: "#f5f5f5",
                              "&:hover": {
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            {/* Shipment Name and Status */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography sx={{ fontWeight: 600 }}>
                                {shipment.shipment_name}
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!!shipment.shipment_status}
                                    name="Mark Shipped"
                                    onChange={(e) => {
                                      setSelectedShipment(shipment);
                                      setIsShipped(e.target.checked);
                                      if (
                                        unshippedCount === 1 &&
                                        e.target.checked === true
                                      ) {
                                        setShowConfirmStatusModal(true);
                                      } else {
                                        setShowConfirmModal(true);
                                      }
                                    }}
                                  />
                                }
                                label="Mark Shipped"
                                labelPlacement="start"
                              />
                            </Box>

                            {/* Shipping Address */}
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="body1"
                                gutterBottom
                                sx={{ display: "inline", marginRight: "6px" }}
                              >
                                Ship To :
                              </Typography>

                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: "inline" }}
                              >
                                {formData.attention_to},&nbsp;
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: "inline" }}
                              >
                                {formData.organization_name},&nbsp;
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: "inline" }}
                              >
                                {formData.address_line_one},&nbsp;
                              </Typography>
                              {formData.address_line_two && (
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{ display: "inline" }}
                                >
                                  {formData.address_line_two},&nbsp;
                                </Typography>
                              )}
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: "inline" }}
                              >
                                {`${formData.city}, ${formData.state}, ${formData.zipcode}`}
                              </Typography>
                            </Box>

                            {/* Contact Details */}
                            <Box sx={{ mb: 1.5 }}>
                              <Typography>
                                <Typography
                                  variant="body1"
                                  gutterBottom
                                  sx={{ display: "inline", marginRight: "6px" }}
                                >
                                  Phone :
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{ display: "inline" }}
                                >
                                  {formData.phone}
                                </Typography>
                              </Typography>

                              <Typography>
                                <Typography
                                  variant="body1"
                                  gutterBottom
                                  sx={{ display: "inline", marginRight: "6px" }}
                                >
                                  Email :
                                </Typography>

                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{ display: "inline" }}
                                >
                                  {formData.email}
                                </Typography>
                              </Typography>
                            </Box>

                            {/* Tracking ID and Tracking URL */}
                            <Box sx={{ mb: 1.5 }}>
                              {/* Show Tracking ID if available */}
                              {shipment.tracking_id && (
                                <Typography>
                                  <Typography
                                    variant="body1"
                                    gutterBottom
                                    sx={{
                                      display: "inline",
                                      marginRight: "6px",
                                    }}
                                  >
                                    Tracking ID :
                                  </Typography>

                                  <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{ display: "inline" }}
                                  >
                                    {shipment.tracking_id}
                                  </Typography>
                                </Typography>
                              )}

                              {/* Show Tracking URL if available */}
                              {shipment.tracking_url && (
                                <Typography>
                                  <Typography
                                    variant="body1"
                                    gutterBottom
                                    sx={{
                                      display: "inline",
                                      marginRight: "6px",
                                    }}
                                  >
                                    Tracking URL :
                                  </Typography>

                                  <Tooltip title="Copy URL" arrow>
                                    <a
                                      href={
                                        shipment.tracking_url.startsWith("http")
                                          ? shipment.tracking_url
                                          : `https://${shipment.tracking_url}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        ...linkStyle,
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleCopyToClipboard(
                                          shipment.tracking_url
                                        );
                                      }}
                                    >
                                      {shipment.tracking_url}
                                      <ContentCopyIcon
                                        fontSize="small"
                                        style={{
                                          marginLeft: 4,
                                          verticalAlign: "middle",
                                        }}
                                      />
                                    </a>
                                  </Tooltip>
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, mb: 2 }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <TextField
                                label="Tracking ID"
                                size="small"
                                value={trackingInputs[shipment.shipment_name]?.trackingId || ""}
                                onChange={(e) =>
                                  setTrackingInputs((prev) => ({
                                    ...prev,
                                    [shipment.shipment_name]: {
                                      ...prev[shipment.shipment_name],
                                      trackingId: e.target.value,
                                    },
                                  }))
                                }
                                fullWidth
                              />
                              <TextField
                                label="Tracking URL"
                                size="small"
                                value={trackingInputs[shipment.shipment_name]?.trackingUrl || ""}
                                onChange={(e) =>
                                  setTrackingInputs((prev) => ({
                                    ...prev,
                                    [shipment.shipment_name]: {
                                      ...prev[shipment.shipment_name],
                                      trackingUrl: e.target.value,
                                    },
                                  }))
                                }
                                fullWidth
                              />
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField
                                label="Comment"
                                size="small"
                                value={trackingInputs[shipment.shipment_name]?.comment || ""}
                                onChange={(e) =>
                                  setTrackingInputs((prev) => ({
                                    ...prev,
                                    [shipment.shipment_name]: {
                                      ...prev[shipment.shipment_name],
                                      comment: e.target.value,
                                    },
                                  }))
                                }
                                fullWidth
                              />
                              <Button
                                variant="contained"
                                onClick={() => handleSaveTracking(shipment.shipment_name)}
                                disabled={
                                  !trackingInputs[shipment.shipment_name]?.trackingId ||
                                  !trackingInputs[shipment.shipment_name]?.trackingUrl
                                }
                                sx={{ minWidth: "100px" }}
                              >
                                Save
                              </Button>
                            </Box>
                            </Box>

                            <Accordion
                              sx={{
                                boxShadow: "none",
                                border: "1px solid #c7c7c7",
                                width: "100%",
                                marginBottom: "10px",
                              }}
                            >
                              <AccordionSummary
                                sx={{
                                  height: "30px",
                                  minHeight: "30px",
                                  "&.Mui-expanded": {
                                    minHeight: "30px",
                                  },
                                }}
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={"panel1-content"}
                                id={"panel1-header"}
                              >
                                <Typography variant="h6" color="primary">
                                  Assigned Inventory Codes
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails>
                                <Box
                                  sx={{ maxHeight: "200px", overflowY: "auto" }}
                                >
                                  {shipment.assigned_inventory_codes.length > 0 &&
                                    shipment.assigned_inventory_codes?.map(
                                      (code, codeIndex) => {
                                        const codeKey = Object.keys(code)[0];
                                        const isChecked = code[codeKey];
                                        return (
                                          <FormControlLabel
                                            key={codeIndex}
                                            control={
                                              <Checkbox
                                                checked={isChecked}
                                                onChange={() => {
                                                  setShipmentDetails(
                                                    (prevDetails) =>
                                                      prevDetails.map(
                                                        (detail) => {
                                                          if (
                                                            detail.shipment_name ===
                                                            shipment.shipment_name
                                                          ) {
                                                            return {
                                                              ...detail,
                                                              assigned_inventory_codes:
                                                                detail.assigned_inventory_codes.map(
                                                                  (c, i) =>
                                                                    i ===
                                                                    codeIndex
                                                                      ? {
                                                                          [codeKey]:
                                                                            !isChecked,
                                                                        }
                                                                      : {
                                                                          [codeKey]:
                                                                            isChecked,
                                                                        }
                                                                ),
                                                            };
                                                          }
                                                          return detail;
                                                        }
                                                      )
                                                  );
                                                }}
                                              />
                                            }
                                            label={codeKey}
                                            disabled={
                                              shipment.assigned_inventory_codes
                                                .length === 1 ||
                                              shipment.shipment_status
                                            }
                                          />
                                        );
                                      }
                                    )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>

                            {/* Action Buttons */}
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                variant="outlined"
                                disabled={!shipment || shipment.shipment_status}
                                onClick={() =>
                                  unassignInventoryCodes(shipment.shipment_name)
                                }
                              >
                                Unassign
                              </Button>

                              <Button
                                variant="outlined"
                                component="label"
                                disabled={shipment.shipment_status}
                              >
                                Upload Manifest
                                <input
                                  type="file"
                                  name="file"
                                  accept=".xlsx"
                                  hidden
                                  onChange={(e) =>
                                    uploadManifestHandler(
                                      e,
                                      shipment.shipment_name
                                    )
                                  }
                                />
                              </Button>

                              {shipment.file_key && (
                                <Tooltip
                                  title="Download File"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    downloadManifestHandler(shipment.file_key)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      downloadManifestHandler(shipment.file_key);
                                    }
                                  }}
                                  tabIndex={0}
                                >
                                  <IconButton sx={{ padding: "6px" }}>
                                    <SaveAltIcon color="green"></SaveAltIcon>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      )
                  )}
                </Grid>
                {/* </Box> */}
              </Box>
            </Grid>
          </Grid>

          {/* Confirmation Modal */}
          {showConfirmModal && selectedShipment && (
            <Dialog
              open={showConfirmModal}
              onClose={() => setShowConfirmModal(false)}
              aria-labelledby="confirm-dialog-title"
              aria-describedby="confirm-dialog-description"
            >
              <Box sx={{ p: 3 }}>
                <Typography
                  id="confirm-dialog-title"
                  variant="h6"
                  sx={{ mb: 2 }}
                >
                  Confirm Selection
                </Typography>
                <Typography id="confirm-dialog-description" sx={{ mb: 3 }}>
                  Are you sure you want to mark as
                  {isShipped ? <b> Shipped </b> : <b> Un-Shipped </b>} this
                  shipment?
                </Typography>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    No
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleMarkAsShipped("partial")}
                  >
                    Yes
                  </Button>
                </Box>
              </Box>
            </Dialog>
          )}

          {/* Confirmation Status Modal */}
          {showConfirmStatusModal && selectedShipment && (
            <Dialog
              open={showConfirmStatusModal}
              onClose={() => setShowConfirmStatusModal(false)}
              aria-labelledby="confirm-status-dialog-title"
              aria-describedby="confirm-status-dialog-description"
            >
              <Box sx={{ p: 3 }}>
                <Typography
                  id="confirm-status-dialog-title"
                  variant="h6"
                  sx={{ mb: 2 }}
                >
                  Confirm Status
                </Typography>
                <Typography
                  id="confirm-status-dialog-description"
                  sx={{ mb: 3 }}
                >
                  Is this the last shipment for this Inquiry?
                </Typography>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      handleMarkAsShipped("incomplete");
                      setShowConfirmStatusModal(false);
                    }}
                  >
                    Incomplete
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleMarkAsShipped("complete")}
                  >
                    Complete
                  </Button>
                </Box>
              </Box>
            </Dialog>
          )}
        </>
      )}
    </>
  );
};

export default UploadManifest;
